import React from 'react';
import {
  Alert,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, GlobeIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useTenantDomainPrices } from '../../hooks/useTenantDomainPrices';
import { formatPrice } from '../../utils/money';
import type { DomainOperation, DomainTld } from '../../types/models/DomainPricing';
// Generic presentational matrix shell (sticky first column + search-by-TLD + pagination), shared with
// the staff Admin → Domain Pricing page. Reused read-only here (no editor cell).
import DomainMatrixTable from '@app/DomainMatrixTable';

// Read-only domain operations pricing matrix for the in-scope client (the /prices/domains routes
// resolve via the impersonation header). Editing lives in the staff Admin → Domain Pricing page.
function TenantDomainPricesTab() {
  const { operations, tlds, priceMap, loading, error, refetch } = useTenantDomainPrices();

  const renderCell = (op: DomainOperation, tld: DomainTld) => {
    const cell = priceMap.get(`${op.slug}:${tld.tld}`);
    if (!cell) {
      return <Content component="small">—</Content>;
    }
    return (
      <>
        {formatPrice(cell.unit_price)}
        {cell.is_custom && (
          <Label isCompact color="blue" className="pf-v6-u-ml-xs">
            Custom
          </Label>
        )}
      </>
    );
  };

  if (loading && operations.length === 0) {
    return <CenteredSpinner />;
  }

  if (error && operations.length === 0) {
    return (
      <EmptyState
        titleText="Unable to load domain prices"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={() => void refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  const refreshButton = (
    <Button
      variant="secondary"
      icon={<SyncAltIcon />}
      onClick={() => void refetch()}
      isLoading={loading}
      isDisabled={loading}
    >
      Refresh
    </Button>
  );

  return (
    <>
      {/* A failed manual Refresh keeps the (stale) matrix visible — surface the error inline rather
          than silently leaving stale prices on screen (this page has no notification drawer). */}
      {error && (
        <Alert variant="danger" isInline title="Could not refresh domain prices" className="pf-v6-u-mb-md">
          {error}
        </Alert>
      )}
      <DomainMatrixTable
        operations={operations}
        tlds={tlds}
        ariaLabel="Domain operation prices matrix"
        renderCell={renderCell}
        toolbarActions={refreshButton}
        emptyState={
          <EmptyState titleText="No domain prices" icon={GlobeIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>No domain operation prices are configured for your organization yet.</EmptyStateBody>
          </EmptyState>
        }
      />
    </>
  );
}

export default TenantDomainPricesTab;
